import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';
import _ from 'underscore';
import * as styles from '../../styles/userNotificationSettingsStyles';
import { Messages } from '../../../codegen/Localize';
import { buttonCell } from '../../../userSettings/styles/EmailAndDisplayNameFormStyles';
import { UserNotificationsSettingsFormUtils } from './UserNotificationsSettingsFormUtils';
import { UserNotificationSettingsActions } from '../../redux/UserNotificationSettingsActions';
import { UserNotificationSettingsLayer } from '../../models/UserNotificationSettingsLayer';
import { defaultMemoize } from 'reselect';
import { AppState } from '../../../app/state/AppState';
import { connect, Dispatch } from 'react-redux';
import { MapDispatchToProps, MapStateToProps } from '../../../app/utils/ConnectUtils';
import { Loadable, LoadingStatus } from '../../../ts/models/Loadable';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { UserNotificationsSettingsGrid } from './UserNotificationsSettingsGrid';
import { IUserNotificationSetting, IUserNotificationSettingsInfo } from '../../models/IUserNotificationsSettingsInfo';
import { useSettingsCoordinator } from '../../../settings/components/settings-coordinator/SettingsCoordinatorHook';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { InlineCancelAndSaveButtons } from '~src/userSettings/components/UserSettingEditButtons';
import { UserSettingsStyle } from '~src/userSettings/styles/UserSettingsStyle';

export interface UserNotificationsSettingsFormPropsFromReduxDispatch {
  readonly actions: UserNotificationSettingsActions;
}

export interface UserNotificationsSettingsFormPropsFromReduxState {
  readonly settings: Loadable<IUserNotificationSettingsInfo>;
}

export interface UserNotificationsSettingsFormOwnProps {
  readonly layer: UserNotificationSettingsLayer;
  readonly siteId?: string;
}

export type UserNotificationsSettingsFormProps =
  UserNotificationsSettingsFormPropsFromReduxDispatch &
  UserNotificationsSettingsFormPropsFromReduxState &
  UserNotificationsSettingsFormOwnProps;

type FormFields = { [bucketAndChannel: string]: boolean };

export class UserNotificationsSettingsFormComponent extends React.PureComponent<UserNotificationsSettingsFormProps> {

  constructor(props: UserNotificationsSettingsFormProps) {
    super(props);
  }

  private async handleSubmit(values: FormFields, formik: FormikHelpers<FormFields>) {
    const settings: IUserNotificationSetting[] = _.keys(values)
      .map((key) => {
        const keyAttributes = UserNotificationsSettingsFormUtils.fromSettingKey(key);
        return {
          enabled: values[key],
          ...keyAttributes
        };
      })
      .filter(setting => _.isUndefined(_.findWhere(this.props.settings.content.settings, setting)));

    this.props.actions.updateSettings({ layer: this.props.layer, siteId: this.props.siteId, updatedSettings: settings });
  }

  componentDidMount() {
    this.props.actions.getSettings(this.props);
  }

  render() {
    if (this.props.settings.loadingStatus === LoadingStatus.FAILED) {
      return <LoadingFailedUI {...this.props} />;
    }
    if (this.props.settings.loadingStatus !== LoadingStatus.LOADED) {
      return null;
    }

    let settings = this.props.settings.content;

    const initialValues = this.getInitialValues(settings.settings);

    let layout = getLayout(this.props.layer);

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values: FormFields, formik: FormikHelpers<FormFields>) => {
          return this.handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<FormFields>) => (
          layout({
            formik: formik,
            settings: settings
          })
        )}
      </Formik>
    );
  }

  private getInitialValues(settings: IUserNotificationSetting[]): FormFields {
    let result: FormFields = {};
    settings.forEach(setting => {
      result[UserNotificationsSettingsFormUtils.toSettingKey(setting)] = setting.enabled;
    });
    return result;
  }
}

function SiteSettingsGrid(props: { settings: IUserNotificationSettingsInfo }) {
  // The form is a nested form on the site settings page, standalone on the user settings page...
  useSettingsCoordinator();
  return <UserNotificationsSettingsGrid settings={props.settings} />;
}

function getLayout(layer: UserNotificationSettingsLayer) {
  return layer === UserNotificationSettingsLayer.User
    ? (FeatureFlags.userSettingMigration ? MigratedUserFormLayout : UserFormLayout)
    : SiteFormLayout;
}

function LoadingFailedUI(props: Pick<UserNotificationsSettingsFormProps, 'layer'>) {
  const layout = getLayout(props.layer);

  return layout({
    mainContent: (
      <div style={{ textAlign: 'center', color: CommonStyles.Colors.Grey77, margin: 28 }}>
        <div style={{ fontSize: 18, marginBottom: 28 }}>{Messages.userNotificationSettingsLoadError()}</div>
        <div style={{ fontSize: 14 }}>{Messages.userNotificationTryAgainLater()}</div>
      </div>
    ),
  });
}

interface LayoutProps {
  formik?: FormikProps<FormFields>;
  settings?: IUserNotificationSettingsInfo;
  mainContent?: JSX.Element;
}

function UserFormLayout(props: LayoutProps) {
  return (
    <table>
      <tbody>
        <tr>
          <td className='tb-details-value-multi-line'>
            <div className={styles.gridContainer}>
              {props.mainContent || (
                <>
                  <UserNotificationsSettingsGrid settings={props.settings} />
                  <ExplanationBlock />
                </>
              )}
            </div>
          </td>
          <td className={`tb-details-button-cell ${buttonCell} ${styles.buttonCell}`}>
            <ButtonWidget
              type='submit'
              buttonType={ButtonType.Outline}
              testId='user-notifications-settings-submit'
              disabled={!props.formik?.dirty || !props.formik?.isValid || props.formik.isSubmitting}
              onClick={props.formik?.submitForm}>
              {Messages.saveChanges()}
            </ButtonWidget>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function MigratedUserFormLayout(props: LayoutProps) {
  return (
    <table>
      <tbody>
        <tr>
          <td className={UserSettingsStyle.labelTextNew}> {Messages.userSettings_Preference_Header()} </td>
          <td className='tb-details-value-multi-line'>
            <div className={styles.gridContainerUserSettings}>
              {props.mainContent || (
                <>
                  <UserNotificationsSettingsGrid settings={props.settings} />
                  <ExplanationBlock />
                </>
              )}
            </div>
          </td>
        </tr>
        {
          showButtons(props.formik) && <>
            <InlineCancelAndSaveButtons
              testId={'user-notifications-settings'}
              formik={props.formik}
            />
          </>
        }
      </tbody>
    </table>
  );
}

function showButtons(formik: FormikProps<FormFields>): boolean {
  return (formik?.dirty && formik?.isValid && !formik?.isSubmitting);
}

const ExplanationBlock = () => <div className={styles.explanationBlock}>{Messages.userNotificationSettingsDisabledStateExplanation()}</div>;

function SiteFormLayout(props: LayoutProps) {
  return (
    <div className={styles.siteSettingsFormContainer}>
      <div className={styles.gridContainer}>
        {props.mainContent || (
          <>
            <SiteSettingsGrid settings={props.settings} />
            <ExplanationBlock />
          </>
        )}
      </div>
    </div>
  );
}

export namespace ReduxConnectedUserNoficationsSettingsForm {

  const mapStateToProps: MapStateToProps<UserNotificationsSettingsFormPropsFromReduxState> = (state: AppState) => ({
    settings: state.notificationSettingsState?.settings
  });

  const mapDispatchToProps: MapDispatchToProps<UserNotificationsSettingsFormPropsFromReduxDispatch> = (dispatch: Dispatch<AppState>) => ({
    actions: memoizedActions(dispatch),
  });

  const memoizedActions = defaultMemoize((dispatch: Dispatch<AppState>) => new UserNotificationSettingsActions(dispatch));
  export const HOC = connect(mapStateToProps, mapDispatchToProps);
}

export const UserNotificationSettingsForm: React.ComponentType<UserNotificationsSettingsFormOwnProps> = ReduxConnectedUserNoficationsSettingsForm.HOC(UserNotificationsSettingsFormComponent);
